import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NsKey } from 'enums/NsKey';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { useSingleFlight } from 'hooks/useSingleFlight';
import { StepPage } from 'features/subscription-process/navigation/Steps/StepPage';
import { useGroupContractProcess } from '../contexts/GroupContractProcessContext';
import { GroupContractStepIdentifier } from '../types/enums';
import { BrokersSelector, BrokersSelectorHandle } from '../selectors/BrokersSelector';

export const BrokersStep = () => {
  const { t } = useDynamicTranslation([NsKey.Contract, NsKey.Common]);
  const { groupContractId, process, refresh, buildStepRoute } = useGroupContractProcess();
  const navigate = useNavigate();
  const selectorRef = useRef<BrokersSelectorHandle>(null);
  const [error, setError] = useState<string | null>(null);

  // Garde : en mode broker, le back omet l'étape COURTIERS de process.steps.
  // Si on arrive ici par URL, on redirige vers Contractualisation.
  useEffect(() => {
    if (!process) return;
    const hasBrokersStep = process.steps.some(
      s => s.identifier === GroupContractStepIdentifier.Brokers,
    );
    if (!hasBrokersStep) {
      navigate(buildStepRoute(GroupContractStepIdentifier.Contractualization), { replace: true });
    }
  }, [process, navigate, buildStepRoute]);

  const saveAndGoNext = useCallback(async () => {
    if (!selectorRef.current) return;
    setError(null);
    try {
      await selectorRef.current.save();
      await refresh();
      navigate(buildStepRoute(GroupContractStepIdentifier.Contractualization));
    } catch (err: any) {
      setError(err?.message || t('GROUP_CONTRACT_PROCESS.BROKERS_STEP.ERROR_SAVE'));
    }
  }, [refresh, navigate, buildStepRoute, t]);

  // Un seul save en vol : les clics répétés sur "Suivant" sont ignorés
  // (sinon writes concurrents sur les habilitations → conflit côté back).
  const { run: handleNext, isInFlight: isSubmitting } = useSingleFlight(saveAndGoNext);

  return (
    <StepPage
      prevConfig={{
        label: t('GROUP_CONTRACT_PROCESS.ACTIONS.PREVIOUS'),
        onClick: () => navigate(buildStepRoute(GroupContractStepIdentifier.Products)),
      }}
      nextConfig={{
        label: isSubmitting
          ? t('GROUP_CONTRACT_PROCESS.ACTIONS.SAVING')
          : t('GROUP_CONTRACT_PROCESS.ACTIONS.NEXT'),
        onClick: handleNext,
        disabled: isSubmitting,
      }}
    >
      <div className="flex flex-col gap-4 max-w-3xl mx-auto">
        <h2 className="text-lg font-semibold">
          {t('GROUP_CONTRACT_PROCESS.BROKERS_STEP.TITLE')}
        </h2>
        <p className="text-sm text-gray-600">
          {t('GROUP_CONTRACT_PROCESS.BROKERS_STEP.DESCRIPTION')}
        </p>

        <BrokersSelector ref={selectorRef} groupContractId={groupContractId} />

        {error && <div className="text-sm text-red-600">{error}</div>}
      </div>
    </StepPage>
  );
};
